import React from 'react';
import * as Yup from 'yup';
import { DialogActions, DialogContent } from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import type { TextField } from '../../modifiers/inputHelpers';
import CbButton from '../../../../../../../../../components/Buttons/CbButton';
import type { Languages } from '../../../../../../../../../i18n/i18n.language-config';
import {
  ControlledLanguageCurrencyField,
  stripSpecialChars,
} from './shared/common';
import type { FactorRule } from '../../../../../api/types/rater-x-preset';

const ReverseIrpmModal = ({ data, close }: ReverseIrpmModalProps) => {
  const formMethods = useForm<FormValues>({
    defaultValues: {
      targetTotalPremium: data.totalPremium.toFixed(2),
    },
    resolver: yupResolver(deriveSchema(data.rules.targetTotalPremium)),
  });

  const onSubmit = (values: FormValues) => {
    data.handleChange({
      value: {
        props: {
          targetTotalPremium: {
            value: values.targetTotalPremium,
          },
          factor: {
            value: data.value.props.factor.value,
          },
        },
      },
    });

    close();
  };

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={formMethods.handleSubmit(onSubmit)}>
        <DialogContent style={{ padding: '1rem 7rem' }}>
          <ControlledLanguageCurrencyField
            name="targetTotalPremium"
            label="Target Indicated Pricing"
            language={data.language}
          />

          <i>
            Due to rounding, final indicated pricing may vary by a few cents.
          </i>
        </DialogContent>
        <DialogActions>
          <CbButton onClick={close} styleName="cancel">
            Cancel
          </CbButton>
          <CbButton type="submit" styleName="ctaButton">
            Confirm
          </CbButton>
        </DialogActions>
      </form>
    </FormProvider>
  );
};

export default ReverseIrpmModal;

export const ReverseIrpmModalConfig = {
  ReverseIrpmModal: {
    component: ReverseIrpmModal,
    config: {
      title: 'Calculate IRPM',
      fullWidth: true,
      maxWidth: 'sm',
    },
  },
};

type ReverseIrpmModalProps = {
  data: {
    language: Languages;
    totalPremium: number;
  } & React.ComponentProps<typeof TextField>;
  close: () => void;
};

const deriveSchema = (rules: FactorRule) =>
  Yup.object().shape({
    targetTotalPremium: Yup.number()
      .transform(stripSpecialChars)
      .min(rules.from!, `Value must be at least ${rules.from}`)
      .max(rules.to!, `Value cannot be higher than ${rules.to}`)
      .test(
        'is-multiple-of-step',
        `Value must be a multiple of ${rules.step}`,
        (value) => (value * 100) % 1 === 0
      )
      .required('Target Indicated Pricing is required'),
  });

type FormValues = {
  targetTotalPremium: string | number;
};
